// src/pages/student-portal/courses/MyCoursesPage.js
// Student's enrolled courses page - Student Portal version (purple theme)

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    Button,
    LinearProgress,
    Chip,
    Skeleton,
    Alert,
    CircularProgress,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#8b5cf6';

const CARD_GRADIENTS = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
];

function getGradient(index) {
    return CARD_GRADIENTS[index % CARD_GRADIENTS.length];
}

function getDifficultyConfig(level) {
    const normalized = (level || '').toLowerCase().trim();
    if (normalized === 'beginner' || normalized === 'easy') {
        return { label: 'Beginner', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' };
    }
    if (normalized === 'intermediate' || normalized === 'medium') {
        return { label: 'Intermediate', color: '#d97706', bg: '#fffbeb', border: '#fde68a' };
    }
    if (normalized === 'advanced' || normalized === 'hard' || normalized === 'expert') {
        return { label: 'Advanced', color: '#e11d48', bg: '#fff1f2', border: '#fecdd3' };
    }
    return null;
}

function formatDuration(minutes) {
    if (!minutes) return null;
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs === 0) return `${mins}m`;
    if (mins === 0) return `${hrs}h`;
    return `${hrs}h ${mins}m`;
}

function formatLastAccessed(dateStr) {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getCourseStatus(course) {
    const progress = course.progress_percentage ?? course.progress ?? 0;
    if (course.status === 'completed' || progress >= 100) return 'completed';
    if (course.status === 'active' || course.status === 'in_progress' || progress > 0) return 'active';
    return 'active';
}

// ─── Hero Stats Card ──────────────────────────────────────────────────
const StatCard = ({ icon: Icon, value, label, loading }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            bgcolor: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(12px)',
            borderRadius: '14px',
            border: '1px solid rgba(255,255,255,0.12)',
            px: 2.5,
            py: 1.5,
            minWidth: 140,
            flex: { xs: '1 1 calc(50% - 8px)', sm: '0 0 auto' },
        }}
    >
        <Box
            sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: 'rgba(255,255,255,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}
        >
            <Icon sx={{ color: 'rgba(255,255,255,0.9)', fontSize: 20 }} />
        </Box>
        <Box>
            {loading ? (
                <Skeleton variant="text" width={32} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
            ) : (
                <Typography sx={{ fontFamily, color: 'white', fontWeight: 800, fontSize: '1.35rem', lineHeight: 1.1 }}>
                    {value}
                </Typography>
            )}
            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.3 }}>
                {label}
            </Typography>
        </Box>
    </Box>
);

// ─── Tab Pill Button ──────────────────────────────────────────────────
const TabPill = ({ label, count, active, onClick }) => (
    <Button
        onClick={onClick}
        disableRipple
        sx={{
            fontFamily,
            fontWeight: 600,
            fontSize: '0.85rem',
            textTransform: 'none',
            borderRadius: '100px',
            px: 2.5,
            py: 0.9,
            minWidth: 0,
            whiteSpace: 'nowrap',
            lineHeight: 1.5,
            border: active ? 'none' : '1px solid #e2e8f0',
            background: active ? 'linear-gradient(135deg, #8b5cf6, #7c3aed)' : 'white',
            color: active ? 'white' : '#64748b',
            boxShadow: active ? '0 4px 14px rgba(139,92,246,0.35)' : 'none',
            transition: 'all 0.25s ease',
            '&:hover': {
                background: active ? 'linear-gradient(135deg, #7c3aed, #6d28d9)' : '#f8fafc',
                boxShadow: active ? '0 6px 20px rgba(139,92,246,0.4)' : '0 2px 8px rgba(0,0,0,0.06)',
                border: active ? 'none' : '1px solid #cbd5e1',
            },
        }}
    >
        {label}
        <Box
            component="span"
            sx={{
                ml: 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 22,
                height: 22,
                borderRadius: '100px',
                bgcolor: active ? 'rgba(255,255,255,0.25)' : '#f1f5f9',
                color: active ? 'white' : '#94a3b8',
                fontSize: '0.7rem',
                fontWeight: 700,
                px: 0.5,
            }}
        >
            {count}
        </Box>
    </Button>
);

// ─── Course Card ──────────────────────────────────────────────────────
const MyCourseCard = ({ course, index }) => {
    const navigate = useNavigate();

    const progress = course.progress_percentage ?? course.progress ?? 0;
    const isCompleted = getCourseStatus(course) === 'completed';
    const courseIdentity = course.course_identity || course.course_id || course.id;
    const title = course.title || course.course_title || 'Untitled Course';
    const description = course.short_description || '';
    const category = course.category || '';
    const difficultyConfig = getDifficultyConfig(course.difficulty_level);
    const moduleCount = course.module_count ?? course.modules_count ?? null;
    const duration = formatDuration(course.estimated_duration_minutes);
    const lastAccessed = formatLastAccessed(course.last_accessed);
    const thumbnail = course.thumbnail_url || course.thumbnail;

    const handleNavigate = () => {
        navigate(`/student-portal/courses/${courseIdentity}`);
    };

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '16px',
                overflow: 'hidden',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 20px 40px rgba(139,92,246,0.12)',
                    borderColor: '#c4b5fd',
                },
            }}
            onClick={handleNavigate}
        >
            {/* Thumbnail / Gradient Fallback */}
            <Box
                sx={{
                    height: 160,
                    position: 'relative',
                    overflow: 'hidden',
                    ...(thumbnail
                        ? {
                            backgroundImage: `url(${thumbnail})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                        }
                        : {
                            background: getGradient(index),
                        }),
                }}
            >
                {!thumbnail && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <SchoolIcon sx={{ fontSize: 52, color: 'rgba(255,255,255,0.35)' }} />
                    </Box>
                )}

                {/* Status Badge */}
                <Chip
                    icon={
                        isCompleted ? (
                            <CheckCircleIcon sx={{ color: 'white !important', fontSize: 14 }} />
                        ) : (
                            <PlayArrowIcon sx={{ color: 'white !important', fontSize: 14 }} />
                        )
                    }
                    label={isCompleted ? 'Completed' : 'In Progress'}
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: 12,
                        left: 12,
                        bgcolor: isCompleted ? '#10b981' : '#8b5cf6',
                        color: 'white',
                        fontFamily,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 26,
                        '& .MuiChip-icon': { ml: '6px' },
                    }}
                />

                {/* Certificate badge */}
                {course.is_certificate_enabled && isCompleted && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: 'rgba(0,0,0,0.4)',
                            backdropFilter: 'blur(8px)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <EmojiEventsIcon sx={{ fontSize: 16, color: '#fbbf24' }} />
                    </Box>
                )}
            </Box>

            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2.5, pb: '12px !important' }}>
                {/* Category */}
                {category && (
                    <Typography
                        sx={{
                            fontFamily,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: ACCENT,
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            mb: 0.5,
                        }}
                    >
                        {category}
                    </Typography>
                )}

                {/* Title */}
                <Typography
                    sx={{
                        fontFamily,
                        fontWeight: 700,
                        fontSize: '1.05rem',
                        color: '#1e293b',
                        lineHeight: 1.35,
                        mb: 0.75,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    {title}
                </Typography>

                {/* Description */}
                {description && (
                    <Typography
                        sx={{
                            fontFamily,
                            fontSize: '0.82rem',
                            color: '#64748b',
                            lineHeight: 1.5,
                            mb: 1.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {description}
                    </Typography>
                )}

                {/* Difficulty + Meta Row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                    {difficultyConfig && (
                        <Chip
                            label={difficultyConfig.label}
                            size="small"
                            icon={
                                difficultyConfig.label === 'Advanced' ? (
                                    <LocalFireDepartmentIcon sx={{ color: `${difficultyConfig.color} !important`, fontSize: 14 }} />
                                ) : undefined
                            }
                            sx={{
                                fontFamily,
                                fontWeight: 600,
                                fontSize: '0.68rem',
                                height: 24,
                                bgcolor: difficultyConfig.bg,
                                color: difficultyConfig.color,
                                border: `1px solid ${difficultyConfig.border}`,
                                '& .MuiChip-icon': { ml: '4px' },
                            }}
                        />
                    )}
                    {moduleCount !== null && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <AutoStoriesIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                                {moduleCount} module{moduleCount !== 1 ? 's' : ''}
                            </Typography>
                        </Box>
                    )}
                    {duration && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <AccessTimeIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                                {duration}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Progress Bar */}
                <Box sx={{ mt: 'auto' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
                            Progress
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily,
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                color: isCompleted ? '#10b981' : ACCENT,
                            }}
                        >
                            {Math.round(progress)}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(progress, 100)}
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: '#f1f5f9',
                            '& .MuiLinearProgress-bar': {
                                bgcolor: isCompleted ? '#10b981' : ACCENT,
                                borderRadius: 3,
                            },
                        }}
                    />
                </Box>

                {/* Last Accessed */}
                {lastAccessed && (
                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', mt: 1 }}>
                        Last accessed {lastAccessed}
                    </Typography>
                )}
            </CardContent>

            {/* Action Button */}
            <Box sx={{ px: 2.5, pb: 2.5, pt: 0.5 }}>
                <Button
                    fullWidth
                    variant={isCompleted ? 'outlined' : 'contained'}
                    endIcon={isCompleted ? <EmojiEventsIcon sx={{ fontSize: '16px !important' }} /> : <ArrowForwardIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleNavigate();
                    }}
                    sx={{
                        fontFamily,
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        textTransform: 'none',
                        borderRadius: '12px',
                        py: 1.1,
                        boxShadow: 'none',
                        ...(isCompleted
                            ? {
                                borderColor: '#e2e8f0',
                                color: '#475569',
                                '&:hover': {
                                    borderColor: ACCENT,
                                    color: ACCENT,
                                    bgcolor: '#faf5ff',
                                },
                            }
                            : {
                                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                                    boxShadow: '0 4px 14px rgba(139,92,246,0.35)',
                                },
                            }),
                    }}
                >
                    {isCompleted ? 'Review Course' : 'Continue Learning'}
                </Button>
            </Box>
        </Card>
    );
};

// ─── Card Skeleton ────────────────────────────────────────────────────
const CourseCardSkeleton = () => (
    <Card sx={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <Skeleton variant="rectangular" height={160} animation="wave" />
        <CardContent sx={{ p: 2.5 }}>
            <Skeleton variant="text" width="30%" sx={{ fontSize: '0.7rem', mb: 0.5 }} />
            <Skeleton variant="text" sx={{ fontSize: '1.05rem', mb: 0.3 }} />
            <Skeleton variant="text" width="85%" sx={{ fontSize: '0.82rem', mb: 1.5 }} />
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                <Skeleton variant="rounded" width={72} height={24} sx={{ borderRadius: '100px' }} />
                <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: '100px' }} />
            </Box>
            <Skeleton variant="rounded" height={6} sx={{ borderRadius: 3, mb: 1 }} />
            <Skeleton variant="text" width="40%" sx={{ fontSize: '0.7rem' }} />
        </CardContent>
        <Box sx={{ px: 2.5, pb: 2.5 }}>
            <Skeleton variant="rounded" height={42} sx={{ borderRadius: '12px' }} />
        </Box>
    </Card>
);

// ─── Empty State ──────────────────────────────────────────────────────
const EmptyState = ({ tab, navigate }) => {
    const configs = {
        in_progress: {
            icon: <PlayArrowIcon sx={{ fontSize: 56, color: '#c4b5fd' }} />,
            title: 'No courses in progress',
            description: 'When you start a course, it will appear here. Browse available courses to begin your learning journey.',
        },
        completed: {
            icon: <EmojiEventsIcon sx={{ fontSize: 56, color: '#c4b5fd' }} />,
            title: 'No completed courses yet',
            description: 'Complete your enrolled courses to see them here. Keep learning and you will earn your first completion!',
        },
        all: {
            icon: <SchoolIcon sx={{ fontSize: 56, color: '#c4b5fd' }} />,
            title: 'No courses yet',
            description: 'You have not enrolled in any courses yet. Browse our course catalog to find something that interests you.',
        },
    };

    const config = configs[tab] || configs.all;

    return (
        <Paper
            sx={{
                py: 8,
                px: 4,
                textAlign: 'center',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
                background: 'linear-gradient(180deg, #faf5ff 0%, #ffffff 100%)',
            }}
        >
            <Box
                sx={{
                    width: 88,
                    height: 88,
                    borderRadius: '50%',
                    bgcolor: '#f5f3ff',
                    border: '1px solid #ede9fe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2.5,
                }}
            >
                {config.icon}
            </Box>
            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.15rem', color: '#1e293b', mb: 1 }}>
                {config.title}
            </Typography>
            <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.9rem', lineHeight: 1.6, maxWidth: 420, mx: 'auto', mb: 3 }}>
                {config.description}
            </Typography>
            <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={() => navigate('/student-portal/courses')}
                sx={{
                    fontFamily,
                    fontWeight: 600,
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                    borderRadius: '12px',
                    px: 3.5,
                    py: 1.1,
                    boxShadow: '0 4px 14px rgba(139,92,246,0.3)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                        boxShadow: '0 6px 20px rgba(139,92,246,0.4)',
                    },
                }}
            >
                Browse Courses
            </Button>
        </Paper>
    );
};

// ─── Main Component ───────────────────────────────────────────────────
export default function MyCoursesPage() {
    const navigate = useNavigate();

    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('in_progress');

    const fetchCourses = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axiosInstance.get('/api/lms/portal/my-courses');
            const data = response.data;
            setCourses(data.courses || data.data || data.results || []);
        } catch (err) {
            console.error('Failed to fetch my courses:', err);
            setError(err.response?.data?.message || 'Failed to load your courses. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCourses();
    }, [fetchCourses]);

    // Derived data
    const stats = useMemo(() => {
        const enrolled = courses.length;
        const inProgress = courses.filter((c) => getCourseStatus(c) === 'active').length;
        const completed = courses.filter((c) => getCourseStatus(c) === 'completed').length;

        let overallProgress = 0;
        if (enrolled > 0) {
            const totalProgress = courses.reduce((sum, c) => sum + (c.progress_percentage ?? c.progress ?? 0), 0);
            overallProgress = Math.round(totalProgress / enrolled);
        }

        return { enrolled, inProgress, completed, overallProgress };
    }, [courses]);

    const filteredCourses = useMemo(() => {
        if (activeTab === 'all') return courses;
        if (activeTab === 'in_progress') return courses.filter((c) => getCourseStatus(c) === 'active');
        if (activeTab === 'completed') return courses.filter((c) => getCourseStatus(c) === 'completed');
        return courses;
    }, [courses, activeTab]);

    const tabCounts = useMemo(() => ({
        in_progress: stats.inProgress,
        completed: stats.completed,
        all: stats.enrolled,
    }), [stats]);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* ── Hero Section ────────────────────────────────────────────── */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4c1d95 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                    px: { xs: 2, md: 4 },
                    pt: { xs: 3.5, md: 4.5 },
                    pb: { xs: 4, md: 5 },
                }}
            >
                {/* Subtle decorative shapes */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -60,
                        right: -60,
                        width: 260,
                        height: 260,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(139,92,246,0.15) 0%, transparent 70%)',
                        pointerEvents: 'none',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -80,
                        left: '20%',
                        width: 320,
                        height: 320,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(124,58,237,0.1) 0%, transparent 70%)',
                        pointerEvents: 'none',
                    }}
                />

                <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 1200, mx: 'auto' }}>
                    <Typography
                        sx={{
                            fontFamily,
                            fontWeight: 800,
                            color: 'white',
                            fontSize: { xs: '1.6rem', md: '2.1rem' },
                            lineHeight: 1.2,
                            mb: 0.5,
                        }}
                    >
                        My Courses
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily,
                            color: 'rgba(255,255,255,0.6)',
                            fontSize: { xs: '0.88rem', md: '0.95rem' },
                            mb: 3,
                        }}
                    >
                        Track your learning progress and continue where you left off
                    </Typography>

                    {/* Stats Cards Row */}
                    <Box sx={{ display: 'flex', gap: { xs: 1.5, md: 2 }, flexWrap: 'wrap', mb: 3 }}>
                        <StatCard icon={SchoolIcon} value={stats.enrolled} label="Enrolled" loading={loading} />
                        <StatCard icon={PlayArrowIcon} value={stats.inProgress} label="In Progress" loading={loading} />
                        <StatCard icon={CheckCircleIcon} value={stats.completed} label="Completed" loading={loading} />
                    </Box>

                    {/* Overall Progress Bar */}
                    <Box sx={{ maxWidth: 480 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem', fontWeight: 500 }}>
                                Overall Progress
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'white', fontSize: '0.82rem', fontWeight: 700 }}>
                                {loading ? '--' : `${stats.overallProgress}%`}
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant={loading ? 'indeterminate' : 'determinate'}
                            value={loading ? 0 : stats.overallProgress}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: 'rgba(255,255,255,0.12)',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 4,
                                    background: 'linear-gradient(90deg, #a78bfa, #c084fc, #e879f9)',
                                },
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            {/* ── Content Area ────────────────────────────────────────────── */}
            <Box sx={{ px: { xs: 2, md: 4 }, pb: 5, maxWidth: 1200, mx: 'auto', mt: -1.5 }}>
                {/* Tab Pills */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        mb: 3,
                        flexWrap: 'wrap',
                        position: 'relative',
                        zIndex: 2,
                    }}
                >
                    {[
                        { label: 'In Progress', value: 'in_progress' },
                        { label: 'Completed', value: 'completed' },
                        { label: 'All Courses', value: 'all' },
                    ].map((tab) => (
                        <TabPill
                            key={tab.value}
                            label={tab.label}
                            count={loading ? '-' : tabCounts[tab.value]}
                            active={activeTab === tab.value}
                            onClick={() => setActiveTab(tab.value)}
                        />
                    ))}
                </Box>

                {/* Error Alert */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{
                            mb: 3,
                            borderRadius: '12px',
                            fontFamily,
                            border: '1px solid #fecaca',
                            '& .MuiAlert-message': { fontFamily },
                        }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={fetchCourses}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none' }}
                            >
                                Retry
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                )}

                {/* Loading Skeletons */}
                {loading ? (
                    <Grid container spacing={3}>
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Grid item xs={12} md={6} key={i}>
                                <CourseCardSkeleton />
                            </Grid>
                        ))}
                    </Grid>
                ) : filteredCourses.length === 0 ? (
                    /* Empty State */
                    <EmptyState tab={activeTab} navigate={navigate} />
                ) : (
                    /* Course Cards Grid (2-column) */
                    <Grid container spacing={3}>
                        {filteredCourses.map((course, index) => (
                            <Grid item xs={12} md={6} key={course.id || course.course_id || index}>
                                <MyCourseCard course={course} index={index} />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
        </Box>
    );
}
